// Flags del auth request OIDC original que condicionan la UI del login —
// hoy, la casilla remember-me: sólo se ofrece en logins "normales", no en
// los que existen para confirmar que el usuario sigue ahí o para autorizar
// una integración (ver showRememberMe en pages/LoginPage.tsx).
//
// La página del login se renderiza sobre la URL del auth request, que
// lleva todos los params OIDC (kc_action, prompt, scope...). PERO los
// re-renders por error de credenciales ocurren bajo
// /login-actions/authenticate?... SIN esos params: si sólo miráramos la
// URL, la casilla "reaparecería" tras una contraseña fallida. Usamos el
// mismo patrón de persistencia en sessionStorage keyed por execution que
// `nativeApp` (ver QUERYSTRING_BEHAVIOR.md §1-2): con `redirect_uri`
// presente es un auth request nuevo → se computa de la URL y se persiste;
// sin él es un re-render del mismo flow → se recupera del storage si el
// executionId coincide. Documentación completa: QUERYSTRING_BEHAVIOR.md §11.

import type { KcData } from "./kcData";

export interface AuthRequestFlags {
  // El auth request lleva kc_action=<acción> (App Initiated Action, p.ej.
  // account console → "Actualizar contraseña"). El login que Keycloak
  // interpone delante es una confirmación de identidad sobre la sesión
  // existente — que además conserva su flag rememberMe original, así que
  // la casilla no tendría ningún efecto. OJO: verificado en KC 26.6.1 que
  // el atributo `isAppInitiatedAction` del FTL NO está presente en ese
  // login interpuesto (sólo en la página de la acción), por eso se
  // detecta por URL.
  kcAction: boolean;
  // prompt incluye "login": reautenticación forzada por el cliente. Es lo
  // que envía el signinPopup de reauth de fd3 (GlobalReauthHandler).
  promptLogin: boolean;
  // scope incluye offline_access: autorización de integración/API. El
  // offline token no depende de la sesión SSO del navegador; una sesión
  // recordada de 30 días como efecto colateral no aporta nada.
  offlineAccess: boolean;
}

const NO_FLAGS: AuthRequestFlags = {
  kcAction: false,
  promptLogin: false,
  offlineAccess: false,
};

const SESSION_STORAGE_KEY = "authRequestFlagsDetected";

interface SessionStorageFlags {
  flags: AuthRequestFlags;
  executionId: string;
}

// Igual que en nativeApp.ts: el execution=<uuid> de data.url.loginAction
// liga lo persistido al flow concreto.
function findExecutionId(data: KcData): string | null {
  const loginAction = data.url?.loginAction;
  if (!loginAction) return null;
  const qIdx = loginAction.indexOf("?");
  if (qIdx < 0) return null;
  const sp = new URLSearchParams(loginAction.substring(qIdx));
  return sp.get("execution");
}

function includesToken(value: string | null, token: string): boolean {
  return (value ?? "").split(/[\s+]+/).includes(token);
}

export function getAuthRequestFlags(search: string, data: KcData): AuthRequestFlags {
  const executionId = findExecutionId(data);
  const sp = new URLSearchParams(search);

  if (sp.get("redirect_uri")) {
    // Auth request nuevo: las señales están (o no) en la propia URL.
    const flags: AuthRequestFlags = {
      kcAction: sp.get("kc_action") != null,
      // prompt y scope son listas separadas por espacios (OIDC §3.1.2.1);
      // includesToken evita falsos positivos por substring.
      promptLogin: includesToken(sp.get("prompt"), "login"),
      offlineAccess: includesToken(sp.get("scope"), "offline_access"),
    };
    try {
      if (executionId) {
        const stored: SessionStorageFlags = { flags, executionId };
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stored));
      } else {
        // Sin execution no podemos ligarlo a un flow: mejor no dejar un
        // valor que pueda contaminar otro auth request posterior.
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }
    } catch {
      // sessionStorage puede fallar (incógnito restrictivo...). No
      // bloqueamos el flow por eso.
    }
    return flags;
  }

  // Re-render del mismo flow (p.ej. tras contraseña fallida): recuperamos
  // lo detectado en el auth request original si el execution coincide.
  if (executionId) {
    try {
      const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (raw) {
        const stored = JSON.parse(raw) as SessionStorageFlags;
        if (stored.executionId === executionId && stored.flags) {
          return {
            kcAction: !!stored.flags.kcAction,
            promptLogin: !!stored.flags.promptLogin,
            offlineAccess: !!stored.flags.offlineAccess,
          };
        }
      }
    } catch {
      // Mismo motivo que arriba.
    }
  }
  return NO_FLAGS;
}
